import React from 'react';
import classNames from 'classnames';
import Icon from 'components/styleguide/icon/Icon';

const styles = require('./CardSelector.module.scss');

export type CardSelectorContext = 'talent' | 'company' | 'neutral';
export type CardSelectorRole = 'button' | 'radio';

export interface CardSelectorProps {
  containerClassName?: string;
  context?: CardSelectorContext;
  description?: string;
  href?: string;
  icon: string;
  id?: string;
  method?: 'get' | 'post' | 'put' | 'patch' | 'delete';
  name?: string;
  onClick?: () => void;
  role?: CardSelectorRole;
  selected?: boolean;
  title: string;
  value?: string;
}

const CardSelector: React.FC<CardSelectorProps> = ({
  containerClassName,
  context = 'neutral',
  description,
  href,
  icon,
  id,
  method,
  name,
  onClick,
  role: roleProp,
  selected = false,
  title,
  value,
}) => {
  const wrapperClass = classNames(styles.wrapper, styles[context], containerClassName, {
    [styles.selected]: selected,
  });

  const inner = (
    <>
      {!href && name && value && (
        <input type="hidden" name={name} value={selected ? value : ''} />
      )}
      <div className={styles.content}>
        {icon && (
          <div className={styles.iconContainer}>
            <Icon name={icon} color="grey-700" size="md" aria-hidden="true" />
          </div>
        )}
        <div className={styles.textContainer}>
          <h3 className={styles.title}>{title}</h3>
          {description && <p className={styles.description}>{description}</p>}
        </div>
      </div>
    </>
  );

  if (href) {
    return (
      <a
        id={id}
        className={wrapperClass}
        href={href}
        aria-label={title}
        {...(method && { 'data-method': method })}
      >
        {inner}
      </a>
    );
  }

  const role = roleProp || 'button';
  const selectionAttr = role === 'radio'
    ? { 'aria-checked': selected }
    : { 'aria-pressed': selected };

  return (
    <button
      type="button"
      id={id}
      className={wrapperClass}
      onClick={onClick}
      role={role}
      aria-label={title}
      {...selectionAttr}
    >
      {inner}
    </button>
  );
};

export default CardSelector;
